import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import type { TreatmentSensitivity } from '../../services/quarantineService';

/**
 * Treatment sensitivity notices (#1830). Copper: a warning while a copper
 * protocol is chosen or running. CP: always shown — ReefGuide has no CP
 * protocol, but users treat with CP elsewhere. Never blocks anything.
 */
export function TreatmentSensitivityNotice({ sensitivities, copperProtocol, compact = false, live = 'off' }: {
  sensitivities: readonly TreatmentSensitivity[];
  /** True when the chosen / running protocol uses copper. */
  copperProtocol: boolean;
  compact?: boolean;
  /** 'polite' where the notice appears after a user action (start form); 'off' for static cards. */
  live?: 'polite' | 'off';
}) {
  const { t } = useTranslation();
  const shown = sensitivities.filter((s) => s.treatment === 'cp' || copperProtocol);
  if (shown.length === 0) return null;
  return (
    <Stack gap="2" data-testid="treatment-sensitivity">
      {shown.map((s) => (
        <Alert
          key={`${s.treatment}-${s.group}`}
          tone={s.treatment === 'copper' ? 'warning' : 'info'}
          live={live}
          compact={compact}
          title={t(`quarantine.sensitivity.${s.treatment}.title`, { group: t(`quarantine.sensitivity.groups.${s.group}`) })}
        >
          <Stack gap="1">
            <Text>{t(`quarantine.sensitivity.${s.treatment}.body`)}</Text>
            <Text color="muted" variant="caption">
              {t('reefGuard.sourceLine', { source: s.source, confidence: t(`reefGuard.confidence.${s.confidence}`) })}
            </Text>
          </Stack>
        </Alert>
      ))}
    </Stack>
  );
}
